import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      try {
        const hash = location.hash.substring(1);
        const params = new URLSearchParams(hash);
        const sessionId = params.get('session_id');

        if (!sessionId) {
          console.error('No session ID found in URL hash');
          toast.error('No session ID found');
          navigate('/');
          return;
        }

        console.log('Processing session with ID:', sessionId);

        const response = await axios.get(`${BACKEND_URL}/api/auth/session`, {
          params: { session_id: sessionId },
          withCredentials: true
        });

        const { session_token, user } = response.data;

        if (!session_token || !user) {
          console.error('Invalid response from auth session:', response.data);
          toast.error('Authentication failed - invalid response');
          navigate('/');
          return;
        }

        console.log('Session created successfully for user:', user.email);

        // Store session token - use both cookie and localStorage for reliability
        const isLocalhost = window.location.hostname === 'localhost';
        
        // Always store in localStorage as backup
        localStorage.setItem('session_token', session_token);
        localStorage.setItem('user', JSON.stringify(user));
        
        // Also try to set cookie
        const cookieParts = [
          `session_token=${session_token}`,
          'path=/',
          `max-age=${7 * 24 * 60 * 60}`
        ];
        
        if (!isLocalhost) {
          cookieParts.push('secure');
          cookieParts.push('samesite=lax');
        }
        
        const cookieOptions = cookieParts.join('; ');
        document.cookie = cookieOptions;

        // Verify storage
        const cookieSet = document.cookie.includes('session_token=');
        const localStorageSet = localStorage.getItem('session_token') === session_token;
        console.log('Cookie set:', cookieSet);
        console.log('localStorage set:', localStorageSet);
        console.log('Session token stored:', session_token.substring(0, 20) + '...');

        toast.success(`Welcome ${user.name}!`);
        
        // Navigate to dashboard
        setTimeout(() => {
          navigate('/dashboard', { state: { user }, replace: true });
        }, 100);
      } catch (error) {
        console.error('Auth error:', error);
        console.error('Error details:', error.response?.data);
        toast.error(`Authentication failed: ${error.response?.data?.detail || error.message}`);
        navigate('/');
      }
    };

    processSession();
  }, [location, navigate]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Authenticating...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
